import { z } from 'zod';

/**
 * What the night research job learned about one CPE model, by READING it
 * (operator, 2026-10-21: "research the different models and brands we add to
 * the platform and figure how to configure them"; "learn through discovery of
 * what the onu allows us to read from it"). Nothing here was written to a
 * unit: the job pulls the ACS document of one specimen per model, inventories
 * its tree, runs the platform's own dialect readers over it, and collects the
 * WORDS every unit of the model has reported on its vendor-prefixed leaves —
 * the vocabulary the gates need before a write can be spelled.
 */
export const modelResearchDialectsSchema = z.object({
  dataModel: z.string(),
  ipv6: z.string(),
  ssidBinding: z.string().nullable(),
  lanInterfaceKey: z.string().nullable(),
  vlanKey: z.string().nullable(),
  /** The VLAN leaf sits on the WANConnectionDevice above the connection (Tenda HG10c). */
  vlanOnParent: z.boolean(),
  /** The passphrase leaf the model exposes on a WLAN instance (`KeyPassphrase`, `PreSharedKey.1.KeyPassphrase`, …). */
  passphraseLeaf: z.string().nullable(),
  /** WLAN instance → band as read off Standard / Channel / the vendor band leaf. */
  bands: z.record(z.string(), z.string()),
  serviceListKey: z.string().nullable(),
});
export type ModelResearchDialects = z.infer<typeof modelResearchDialectsSchema>;

export const modelResearchLeafSchema = z.object({
  name: z.string(),
  type: z.string().nullable(),
  writable: z.boolean().nullable(),
});

export const modelResearchSchema = z.object({
  id: z.string(),
  manufacturer: z.string(),
  productClass: z.string(),
  brand: z.string().nullable(),
  softwareVersions: z.array(z.string()),
  deviceCount: z.number().int(),
  /** The unit whose document was inventoried. */
  specimenDeviceId: z.string(),
  researchedAt: z.number().int(),
  /** How many parameters the specimen's document held. */
  parameters: z.number().int(),
  /** Top-level objects under the root (`WANDevice`, `X_HW_Security`, …). */
  rootObjects: z.array(z.string()),
  /** Vendor-prefixed subtrees (`InternetGatewayDevice.X_TDTC_UserInterface`, …) with their leaf counts. */
  vendorSubtrees: z.array(z.object({ path: z.string(), leaves: z.number().int() })),
  dialects: modelResearchDialectsSchema,
  /** The subscriber WAN connection's leaves — names, types and writability, never values. */
  wanLeaves: z.array(modelResearchLeafSchema),
  /**
   * Every DISTINCT value every unit of the model has reported on a
   * vendor-prefixed string leaf with short, enumeration-shaped values —
   * the observed vocabulary (`X_TDTC_IPv6AddressMode`: None, Slaac, Static).
   * Secrets (password, passphrase, key) are never collected.
   */
  words: z.record(z.string(), z.array(z.string())),
  /** What a person has to verify next, in sentences. */
  toVerify: z.array(z.string()),
  /** Which profile this model is managed through today. */
  profileScope: z.enum(['model', 'brand', 'generic', 'none']),
});
export type ModelResearch = z.infer<typeof modelResearchSchema>;

export const modelResearchRunResultSchema = z.object({
  researched: z.array(z.string()),
  skipped: z.array(z.object({ model: z.string(), why: z.string() })),
});
export type ModelResearchRunResult = z.infer<typeof modelResearchRunResultSchema>;
